import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, Loader2, UserCheck } from "lucide-react";
import { getSaleSchedule, setSellerSaleDates } from "@/lib/api";
import { fmtSaleDate } from "@/lib/applyApi";
import { Button } from "@/components/ui/button";
import { DatePick, iso, toDate } from "@/components/adtemplate/DatePick";
import { closeShort } from "@/lib/brand";

function Row({ label, value, testId }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-3 py-2 border-b border-stone-100 last:border-0">
      <dt className="text-[11px] uppercase tracking-wider text-stone-400 font-semibold sm:w-40 shrink-0">{label}</dt>
      <dd data-testid={testId} className="text-sm font-semibold text-stone-900">{value}</dd>
    </div>
  );
}

export function SaleDatesPanel({ horseId, locked, onSaved }) {
  const [data, setData] = useState(null);
  const [start, setStart] = useState();
  const [listing, setListing] = useState();
  const [close, setClose] = useState();
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const d = await getSaleSchedule(horseId);
    setData(d);
    setStart(toDate(d.schedule?.start_date));
    setListing(d.schedule?.listing_date_auto === false ? toDate(d.schedule?.listing_date) : undefined);
    setClose(toDate(d.schedule?.close_date));
  }, [horseId]);
  useEffect(() => { load().catch(() => setData({ schedule: null, allow_seller_dates: false })); }, [load]);
  if (!data) return <Loader2 className="h-4 w-4 animate-spin text-stone-400" />;

  const s = data.schedule;
  const NS = "Not Scheduled Yet";
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const canPick = data.allow_seller_dates && !locked;
  const dirty = close && (iso(close) !== s?.close_date || (start ? iso(start) : null) !== (s?.start_date || null)
    || (listing ? iso(listing) : null) !== (s?.listing_date_auto === false ? s.listing_date : null));

  const save = async () => {
    setBusy(true);
    try {
      const d = await setSellerSaleDates(horseId, { close_date: iso(close), start_date: start ? iso(start) : null, listing_date: listing ? iso(listing) : "auto" });
      setData(d);
      toast.success("Sale dates saved");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save sale dates"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="sale-dates-panel" className="mb-4 rounded-xl bg-stone-50 border border-stone-200 px-4 py-2">
      <dl>
        <Row label="Listing Date" value={s?.listing_date ? `${fmtSaleDate(s.listing_date)}${s.listing_date_auto ? " (based on weeks advertised)" : ""}` : NS} testId="listing-date-display" />
        <Row label="Bidding Start Date" value={s?.start_date ? fmtSaleDate(s.start_date) : NS} testId="sale-start-display" />
        <Row label="Bidding Close Date" value={s?.close_date ? fmtSaleDate(s.close_date) : NS} testId="sale-close-display" />
        <Row label="Sale Close Time" value={s?.close_time_label || data.default_close_label || closeShort()} testId="sale-close-time-display" />
      </dl>
      {s?.set_by === "seller" && <p data-testid="sale-seller-selected" className="text-xs text-blue-800 flex items-center gap-1 pb-2"><UserCheck className="h-3.5 w-3.5" /> Seller-selected dates — our team may adjust them.</p>}
      {!canPick && <p data-testid="sale-dates-admin-note" className="text-xs text-stone-500 pb-2 flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Sale dates are scheduled by Bad Ass Horse Sales. You don't need dates to submit.</p>}
      {canPick && (
        <div data-testid="sale-dates-seller-pick" className="py-3 space-y-2 border-t border-stone-200">
          <p className="text-xs font-semibold text-stone-700">Choose your preferred dates (seller-selected)</p>
          <div className="grid sm:grid-cols-3 gap-2">
            <DatePick testId="seller-listing-date-picker" value={listing} onChange={setListing} placeholder="Listing date (auto from weeks)" disabledBefore={today} />
            <DatePick testId="seller-start-date-picker" value={start} onChange={setStart} placeholder="Bidding start date" disabledBefore={today} />
            <DatePick testId="seller-close-date-picker" value={close} onChange={setClose} placeholder="Bidding close date" disabledBefore={start || today} />
          </div>
          <Button data-testid="seller-save-sale-dates" onClick={save} disabled={!dirty || busy} className="h-10 rounded-full bg-amber-800 hover:bg-amber-900 text-white text-sm">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save Sale Dates
          </Button>
          <p className="text-[11px] text-stone-400">The closing time is always set by our team.</p>
        </div>
      )}
    </div>
  );
}
